import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { Box, Button, MenuItem, Select } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_DRAG_HANDLE_SELECTOR } from "../../../../app/constants";
import { useAppSelector } from "../../../../app/hooks";
import {
  SupplementDefinitions,
  SupplementsData,
} from "../../../../features/supplements/supplementAPI";
import i18n from "../../../../i18n";
import OverlaySpinner from "../overlaySpinner/OverlaySpinner";

interface Props {
  supplements: SupplementsData;
  originalSupplement: { key: string; mode: string } | null;
  isLoading: boolean;
  onClose: () => void;
  handleOnSave: (key: string, mode: string) => void;
  modalTitle: string;
  modalSupplementSelectLabel: string;
  modalSupplementOptionalityLabel: string;
  supplementDefinitions: SupplementDefinitions;
}

const EditSupplements = ({
  supplements,
  originalSupplement,
  isLoading,
  onClose,
  handleOnSave,
  modalTitle,
  modalSupplementOptionalityLabel,
  modalSupplementSelectLabel,
  supplementDefinitions,
}: Props) => {
  const [selectedSupplement, setSelectedSupplement] = useState<string>(
    originalSupplement?.key || "",
  );
  const [selectedOptionality, setSelectedOptionality] = useState<string>(
    originalSupplement?.mode || "",
  );

  const { t } = useTranslation();

  const translatedSupplementNames = useAppSelector(
    (state) => state.localized.supplement,
  );

  const optionalityOptions = [
    { label: t("common:DEFAULT_ON"), value: "DEFAULT_ON" },
    { label: t("common:DEFAULT_OFF"), value: "DEFAULT_OFF" },
    { label: t("common:FORCE"), value: "FORCE" },
  ];

  const selectedSupplementType =
    supplementDefinitions[selectedSupplement]?.type || "";

  const handleSupplementOnChange = (key: string) => {
    if (key) {
      // Update selected supplement state
      setSelectedSupplement(key);

      // Check if the selected supplement type is not ON_OFF
      if (supplementDefinitions[key]?.type !== "ON_OFF") {
        // If not, set optionality to DEFAULT_OFF by default, as user should not be able to change it
        setSelectedOptionality("DEFAULT_OFF");
      }
    }
  };

  // Create an array of supplement keys and translated names
  const supplementArray = Object.keys(supplementDefinitions).map((key) => ({
    key,
    name:
      translatedSupplementNames[key] !== undefined
        ? translatedSupplementNames[key]
        : key,
  }));

  // Sort the array by translated name with the locale
  supplementArray.sort((a, b) => {
    const nameA = a.name || ""; // Use empty string if a.name is undefined
    const nameB = b.name || ""; // Use empty string if b.name is undefined
    return nameA.localeCompare(nameB, i18n.language);
  });

  return (
    <Box
      sx={{
        position: "relative",
        width: "572px",
        display: "flex",
        flexDirection: "column",
        borderTopRightRadius: "12px",
        borderTopLeftRadius: "12px",
      }}
    >
      <OverlaySpinner isLoading={isLoading} sx={{ position: "absolute" }} />
      <Box
        className={DEFAULT_DRAG_HANDLE_SELECTOR}
        sx={{
          fontSize: "16px",
          fontWeight: "600",
          padding: "15px 15px",
        }}
      >
        {modalTitle}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          padding: "15px 15px",
        }}
      >
        <Box
          sx={{
            width: "40%",
            textAlign: "end",
            marginRight: "20px",
            color: "rgba(0, 0, 0, 0.52)",
          }}
        >
          {modalSupplementSelectLabel}
        </Box>
        <Box sx={{ width: "60%" }}>
          <Select
            size="small"
            fullWidth
            displayEmpty
            // Show selectedSupplement as value only if it is not empty and supplementDefinitions is not empty
            value={
              selectedSupplement &&
              Object.keys(supplementDefinitions).length > 0
                ? selectedSupplement
                : ""
            }
            onChange={(e) =>
              e.target.value && handleSupplementOnChange(e.target.value)
            }
            sx={{
              backgroundColor: "rgba(249, 249, 250, 0.6)",
            }}
            disabled={isLoading || originalSupplement?.key !== ""}
            renderValue={(supplement) =>
              supplement
                ? translatedSupplementNames[supplement] || supplement
                : t("common:select...")
            }
          >
            {
              /* Map through the sorted supplementArray */
              supplementArray.map((supplement) => {
                if (
                  !supplements[supplement.key] || // Is not already in supplements
                  originalSupplement?.key === supplement.key || // Is not original supplement (editing)
                  (originalSupplement?.key === "" &&
                    supplements[selectedSupplement]) // Get rid of MUI error when saving
                ) {
                  return (
                    <MenuItem key={supplement.key} value={supplement.key}>
                      {supplement.name}
                    </MenuItem>
                  );
                }
                return null;
              })
            }
          </Select>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "15px 15px",
        }}
      >
        <Box
          sx={{
            whiteSpace: "nowrap",
            width: "40%",
            textAlign: "end",
            marginRight: "20px",
            color: "rgba(0, 0, 0, 0.52)",
          }}
        >
          {modalSupplementOptionalityLabel}
        </Box>
        <Box sx={{ width: "60%" }}>
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={selectedOptionality || ""}
            onChange={(e) =>
              e.target.value ? setSelectedOptionality(e.target.value) : ""
            }
            sx={{
              backgroundColor: "rgba(249, 249, 250, 0.6)",
            }}
            disabled={isLoading || selectedSupplementType !== "ON_OFF"}
            renderValue={(optionality) => {
              const selectedOption = optionalityOptions.find(
                (option) => option.value === optionality,
              );
              return selectedOption
                ? selectedOption.label.charAt(0).toUpperCase() +
                    selectedOption.label.slice(1)
                : t("common:select...");
            }}
          >
            {optionalityOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Box
        className={DEFAULT_DRAG_HANDLE_SELECTOR}
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          padding: "15px 15px",
        }}
      >
        <Button
          sx={{
            borderRadius: "15px",
            height: "30px",
          }}
          startIcon={<CloseIcon />}
          disabled={isLoading}
          onClick={() => onClose()}
          variant="outlined"
        >
          {t("common:cancelShort")}
        </Button>

        <Button
          disabled={isLoading || !selectedSupplement || !selectedOptionality}
          sx={{
            borderRadius: "15px",
            height: "30px",
          }}
          startIcon={<CheckIcon />}
          onClick={() => handleOnSave(selectedSupplement, selectedOptionality)}
          variant="contained"
        >
          {t("common:complete")}
        </Button>
      </Box>
    </Box>
  );
};

export default EditSupplements;
